import { useState, useEffect, useRef } from 'react'
import { useGallery, useGalleries } from '../lib/galleries'
import { motion } from 'framer-motion'
import { useTranslation, Trans } from 'react-i18next'
import { FaImages } from 'react-icons/fa'
import LoadingSpinner from '../components/LoadingSpinner'
import ScrollToTopButton from '../components/ScrollToTopButton'
import MobileQuoteButton from '../components/MobileQuoteButton'
import ConsentCheckbox from '../components/ConsentCheckbox'
import MondayManagement from '../pages/admin/MondayManagement'
import GalleryModal from '../components/GalleryModal'
import { Link } from 'react-router-dom'

const FORM_THANK_YOU_MAP = {
  construction_new_project: '/forms/construction/new-quote/thank-you',
}

const SERVICE_ICONS = [
  '/construction/card 1.webp',
  '/construction/card 2.webp',
  '/construction/card 6.webp',
  '/construction/card 3.webp',
  '/construction/card 4.webp',
  '/construction/card 5.webp',
]

const SERVICE_GALLERY_MAP = {
  2: ['pergola', 'fence', 'concrete'],
}

const DETAIL_IMAGES = [
  '/construction/cover 1.webp',
  '/construction/cover 2.webp',
  '/construction/cover 6.webp',
  '/construction/cover 3.webp',
  '/construction/cover 4.webp',
  '/construction/cover 5.webp',
]


const ConstructionSection = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [showMondayForm, setShowMondayForm] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const [servicesList, setServicesList] = useState([])
  const [serviceDetails, setServiceDetails] = useState([])
  const [activeFormType, setActiveFormType] = useState(null)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [activeGalleryKey, setActiveGalleryKey] = useState(null)
  const preModalPath = useRef(null)
  const { galleries } = useGalleries()
  const { images: activeGalleryImages, loading: galleryLoading } = useGallery(activeGalleryKey)

  const FORM_PATHS = { construction_new_project: '/forms/construction/new-quote' }

  const openModal = (formType) => {
    preModalPath.current = window.location.pathname
    window.history.pushState({ modal: true }, '', FORM_PATHS[formType] || '/forms/construction/new-quote')
    setActiveFormType(formType)
    setShowMondayForm(true)
  }

  const closeModal = () => {
    setShowMondayForm(false)
    window.history.replaceState(null, '', preModalPath.current || '/construction')
  }

  useEffect(() => {
    const handlePopState = () => setShowMondayForm(false)
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])
  const { t } = useTranslation()

  useEffect(() => {
    const loadTranslations = () => {
      const services = t('construction.services_list', { returnObjects: true })
      const details = t('construction.detail_sections', { returnObjects: true })

      // Merge detail sections with images
      const mergedDetails = Array.isArray(details) ? details.map((section, index) => ({
        ...section,
        image: DETAIL_IMAGES[index]
      })) : []

      setServicesList(Array.isArray(services) ? services : [])
      setServiceDetails(mergedDetails)
      setIsLoading(false) // Set loading to false after translations are loaded
      setHasAnimated(true) // Mark as animated to prevent re-triggering
    }

    loadTranslations()
  }, [t])

  // Optional: Preload images in background without affecting loading state
  useEffect(() => {
    const preloadImages = async () => {
      try {
        const imagesToLoad = ['/construction/hero.webp', ...SERVICE_ICONS]
        await Promise.all(
          imagesToLoad.map((src) => {
            return new Promise((resolve, reject) => {
              const img = new Image()
              img.src = src
              img.onload = resolve
              img.onerror = reject
            })
          })
        )
      } catch (error) {
        console.error('Failed to preload images', error)
      }
    }

    preloadImages()
  }, [])

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <section
      id="construction"
      className="text-black bg-white"
      aria-labelledby="construction-heading"
      data-section="construction"
    >
      {/* Hero Section */}
      <div className="relative min-h-screen sm:h-screen sm:max-h-[1080px] w-full overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={hasAnimated ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 1.2 }}
          className="absolute inset-0 w-full h-full"
        >
          <img
            src="/construction/hero.webp"
            alt={t('construction.hero_alt')}
            className="object-cover w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-r to-transparent from-black/90 via-black/40" />
        </motion.div>

        <div className="relative z-10 h-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-20 py-24 flex flex-col justify-end sm:justify-between">
          <div className="max-w-4xl">
            <motion.h1
              initial={{ opacity: 0, y: 50 }}
              animate={hasAnimated ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
              id="construction-heading"
              className="text-4xl sm:text-7xl md:text-8xl lg:text-[7rem] font-black leading-[0.9] tracking-tighter uppercase text-white mb-8 sm:mb-12 lg:mb-16"
            >
              {t('construction.hero_title')}
            </motion.h1>
          </div>

          <div className="flex flex-wrap items-end justify-between w-full gap-6">
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={hasAnimated ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
              className="max-w-2xl text-xl font-medium leading-relaxed text-white sm:text-2xl"
            >
              <Trans
                i18nKey="construction.hero_text_1"
                components={{ 1: <span className="font-bold" />, 3: <span className="font-bold" /> }}
              />
              <br className="hidden sm:block" />
              <br className="hidden sm:block" />
              <Trans
                i18nKey="construction.hero_text_2"
                components={{ 1: <span className="font-bold" /> }}
              />
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={hasAnimated ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.6 }}
              className="hidden sm:flex flex-shrink-0"
            >
              <button
                onClick={() => openModal('construction_new_project')}
                className="inline-flex justify-center px-8 py-4 text-sm font-bold tracking-widest uppercase rounded-lg bg-primary text-black hover:bg-yellow-400 min-w-[220px]"
              >
                {t('construction.open_form_modal')}
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Services Icons Grid */}
      <div className="px-4 py-24 bg-white sm:px-12 lg:px-20">
        <div className="mx-auto space-y-16 max-w-7xl">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={hasAnimated ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="inline-block px-12 py-3 rounded-lg bg-primary"
            >
              <h2 className="text-3xl font-bold tracking-widest text-black uppercase sm:text-4xl">{t('construction.services_title')}</h2>
            </motion.div>
          </div>

          <div className="flex flex-wrap gap-10 justify-center items-stretch">
            {servicesList.map((service, index) => (
              <motion.article
                key={service}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={hasAnimated ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: hasAnimated ? index * 0.1 : 0 }}
                className="group flex flex-col items-center text-center p-8 rounded-3xl border border-gray-200 hover:border-primary hover:shadow-xl transition-all duration-300 bg-white w-full sm:w-[calc(50%-2.5rem)] lg:w-[calc(33.333%-2.5rem)] cursor-pointer"
                onClick={() => {
                  const el = document.getElementById(`construction-service-${index}`)
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    const el = document.getElementById(`construction-service-${index}`)
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                }}
              >
                <div className="relative mb-6 w-24 h-24">
                  <img src={SERVICE_ICONS[index]} alt={service} className="object-contain w-full h-full group-hover:scale-110 transition-transform duration-300" />
                </div>
                <h3 className="text-lg font-bold leading-tight sm:text-xl">{service}</h3>
              </motion.article>
            ))}
          </div>
        </div>
      </div>

      {/* Service Detail Sections */}
      <div className="pb-24 bg-white">
        <div className="max-w-[1920px] mx-auto">
          {serviceDetails.map((detail, index) => (
            <div key={detail.title} id={`construction-service-${index}`} className="px-4 py-16 border-t border-gray-100 sm:py-24 sm:px-12 lg:px-20">
              <div className="grid gap-16 items-center lg:grid-cols-2">
                <motion.div
                  className={`relative h-[400px] sm:h-[500px] lg:h-[600px] overflow-hidden ${index % 2 === 0 ? '' : 'lg:order-2'}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={hasAnimated ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <motion.img
                    src={detail.image}
                    alt={detail.title}
                    className="object-cover w-full h-full"
                    initial={{ filter: 'grayscale(100%)' }}
                    whileInView={{ filter: 'grayscale(0%)' }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                  />
                </motion.div>

                <motion.div
                  className={`space-y-8 ${index % 2 === 0 ? '' : 'lg:order-1'}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                  animate={hasAnimated ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: hasAnimated ? 0.2 : 0 }}
                >
                  <div className={`border-l-4 border-black pl-6 ${index % 2 === 0 ? 'text-left' : 'lg:text-right lg:border-l-0 lg:border-r-4 lg:pr-6'}`}>
                    <h4 className="mb-2 text-3xl font-black leading-tight text-black uppercase sm:text-4xl">{detail.title}</h4>
                  </div>

                  <p className={`text-lg sm:text-xl text-gray-700 leading-relaxed ${index % 2 === 0 ? 'text-left' : 'lg:text-right'}`}>
                    {detail.description}
                  </p>

                  <div className={`${index % 2 === 0 ? 'text-left' : 'lg:text-right'}`}>
                    <h5 className="mb-4 text-lg font-bold">{t('construction.what_includes')}</h5>
                    <ul className={`inline-block space-y-3 text-left`}>
                      {detail.bullets.map((bullet) => (
                        <li key={bullet} className="flex gap-3 items-start text-lg text-gray-600">
                          <span aria-hidden="true" className="text-primary mt-1.5 text-xs">●</span>
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className={`${index % 2 === 0 ? 'text-left' : 'lg:text-right'} space-y-4`}>
                    <div className={`flex flex-wrap gap-4 ${index % 2 === 0 ? 'justify-start' : 'lg:justify-end'}`}>
                      <button
                        onClick={() => {
                          openModal('construction_new_project')
                        }}
                        className="inline-flex justify-center px-8 py-4 text-sm font-bold tracking-widest uppercase rounded-lg bg-primary text-black hover:bg-yellow-400 min-w-[220px]"
                      >
                        {t('construction.open_form_modal')}
                      </button>
                      {SERVICE_GALLERY_MAP[index]?.map(key => {
                        const gal = galleries.find(g => g.service_key === key)
                        if (!gal) return null
                        return (
                          <button
                            key={key}
                            onClick={() => { setActiveGalleryKey(key); setGalleryOpen(true) }}
                            className="inline-flex items-center gap-2 justify-center px-8 py-4 text-sm font-bold tracking-widest uppercase rounded-lg border-2 border-black text-black hover:bg-black hover:text-white transition-all duration-200 min-w-[220px]"
                          >
                            <FaImages />
                            {gal.service_label}
                          </button>
                        )
                      })}
                    </div>
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
                      <Trans i18nKey="construction.custom_quote" components={{ br: <br className="hidden" /> }} />
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Form Section */}
      <div className="px-4 py-24 bg-gray-50 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto bg-white rounded-[3rem] p-8 sm:p-16 shadow-2xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-black text-black sm:text-5xl">{t('construction.form_title')}</h2>
            <p className="text-xl text-gray-500">{t('construction.form_subtitle')}</p>
          </motion.div>

          <div className="grid gap-12 lg:grid-cols-2">
            <form
              action="https://formsubmit.co/info@jdrivero.com"
              method="POST"
              className="contents"
            >
              <input type="hidden" name="_subject" value={t('form.subjects.construction', 'New Construction Quote Request')} />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="_next" value="https://www.jdrivero.com/success" />
              <input type="hidden" name="_captcha" value="false" />

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-8"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <label className="block space-y-3">
                    <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">{t('form.first_name')}</span>
                    <input
                      type="text"
                      name={t('form.first_name_label', 'First Name')}
                      className="py-3 w-full text-lg bg-gray-50 border-b-2 border-gray-200 transition-colors outline-none focus:border-primary"
                      required
                    />
                  </label>
                  <label className="block space-y-3">
                    <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">{t('form.last_name')}</span>
                    <input
                      type="text"
                      name={t('form.last_name_label', 'Last Name')}
                      className="py-3 w-full text-lg bg-gray-50 border-b-2 border-gray-200 transition-colors outline-none focus:border-primary"
                      required
                    />
                  </label>
                </div>

                <label className="block space-y-3">
                  <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">{t('form.email')}</span>
                  <input
                    type="email"
                    name={t('form.email_label', 'Email')}
                    className="py-3 w-full text-lg bg-gray-50 border-b-2 border-gray-200 transition-colors outline-none focus:border-primary"
                    required
                  />
                </label>

                <label className="block space-y-3">
                  <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">{t('form.phone')}</span>
                  <input
                    type="tel"
                    name={t('form.phone_label', 'Phone Number')}
                    pattern="^[+]?[0-9 \\-]{10,15}$"
                    title={t('form.phone_error', 'Phone number must be between 10 and 15 digits.')}
                    className="py-3 w-full text-lg bg-gray-50 border-b-2 border-gray-200 transition-colors outline-none focus:border-primary"
                    required
                  />
                </label>

                <label className="block space-y-3">
                  <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">{t('form.project_details')}</span>
                  <textarea
                    name={t('form.project_details_label', 'Project Details')}
                    rows={4}
                    className="py-3 w-full text-lg bg-gray-50 border-b-2 border-gray-200 transition-colors outline-none resize-none focus:border-primary"
                    required
                  />
                </label>
                <ConsentCheckbox />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="p-8 space-y-8 rounded-3xl bg-primary/10"
              >
                <fieldset className="space-y-6">
                  <legend className="mb-6 text-sm font-bold tracking-widest text-black uppercase">{t('form.type_label')}</legend>
                  {['Residential', 'Commercial', 'Civil'].map((typeKey) => (
                    <label key={typeKey} className="flex gap-4 items-center cursor-pointer group">
                      <div className="flex relative justify-center items-center w-6 h-6 bg-white rounded-full border-2 border-gray-300 transition-colors group-hover:border-primary">
                        <input type="radio" name="projectType" value={typeKey} required className="absolute inset-0 w-full h-full appearance-none cursor-pointer peer" />
                        <div className="w-3 h-3 rounded-full transition-transform scale-0 bg-primary peer-checked:scale-100" />
                      </div>
                      <span className="text-lg font-medium text-gray-700 transition-colors group-hover:text-black">
                        {t(`form.${typeKey.toLowerCase()}`)}
                      </span>
                    </label>
                  ))}
                </fieldset>
                <fieldset className="space-y-6">
                  <legend className="mb-6 text-sm font-bold tracking-widest text-black uppercase">{t('form.service_interest')}</legend>
                  {servicesList.map((service) => (
                    <label key={service} className="flex gap-4 items-center cursor-pointer group">
                      <div className="flex relative justify-center items-center w-6 h-6 bg-white rounded-full border-2 border-gray-300 transition-colors group-hover:border-primary">
                        <input type="radio" name="construction-service" value={service} required className="absolute inset-0 w-full h-full appearance-none cursor-pointer peer" />
                        <div className="w-3 h-3 rounded-full transition-transform scale-0 bg-primary peer-checked:scale-100" />
                      </div>
                      <span className="text-lg font-medium text-gray-700 transition-colors group-hover:text-black">{service}</span>
                    </label>
                  ))}
                </fieldset>
                <button type="submit" className="w-full rounded-2xl bg-primary hover:bg-yellow-400 px-8 py-5 text-black font-black uppercase tracking-[0.2em] shadow-lg transition-all hover:scale-[1.02]">
                  {t('form.send')}
                </button>
              </motion.div>
            </form>
          </div>
        </div>
      </div>
      <GalleryModal images={activeGalleryImages.map(img => img.url)} isOpen={galleryOpen} onClose={() => setGalleryOpen(false)} loading={galleryLoading} />
      <MobileQuoteButton onClick={() => openModal('construction_new_project')} />
      <ScrollToTopButton />

      {/* Monday Form Modal */}
      {showMondayForm && (
        <div className="overflow-y-auto fixed inset-0 z-50">
          <div className="flex justify-center items-center p-4 min-h-screen">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={closeModal}
            />

            {/* Modal Content */}
            <div className="relative bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="overflow-y-auto max-h-[90vh]">
                <MondayManagement
                  session={null}
                  onReturn={closeModal}
                  isPublicMode={true}
                  defaultFormType={activeFormType}
                  thankYouPath={FORM_THANK_YOU_MAP[activeFormType]}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default ConstructionSection
